import { Card, Col, Input, Row } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { itemRoutesUrl } from "features/menu/item.routes";
import TailwindButton from "components/Button";
import { separateMediaUrlsByType } from "utils/Utils";
import { CreateCreatifyLinkWithParams } from "./CreatifyAPI";
import MediaSelectorCard from "./MediaSelectorCard";
import { headerStyles, stepsEnum } from "./options";
/**
 * Default empty function used as placeholder for optional callbacks
 */
const defaultFn = () => { }

/**
 * StepOne component for AI Influencer content creation
 * Handles media selection and content details input for Creatify integration
 */
const StepOne = ({ user,itemInfo, itemId,loading=false, onNext =  defaultFn,  setLoading=defaultFn }) => {
    const router = useHistory()
    const { t } = useTranslation();
    const [mediaUrls, setMediaUrls] = useState([]);
    const [creatifyParams, setCreatifyParams] = useState({});
    const [errors, setErrors] = useState({});

    const { TextArea } = Input;

    useEffect(() => {
        if (itemInfo && Object.keys(itemInfo).length > 0) {
            setMediaUrls(itemInfo?.itemImageURLs ?? []);
            setCreatifyParams({
                ...(itemInfo?.baseLanguage ?? {}),
                title: itemInfo?.baseLanguage?.title || "",
                description: itemInfo?.baseLanguage?.description || "",
                image_urls: [],
                video_urls: [],
            });
        }
    }, [itemInfo]);

    /**
     * Validates form fields and media selection
     * @param {Object} creatifyParams - Form parameters containing title and description
     * @param {Array} mediaUrls - Array of selected media URLs
     * @returns {Object} Object containing validation errors
     */
    const validateFields = (creatifyParams, mediaUrls) => {
        const newErrors = {};

        if (!creatifyParams.title?.trim()) {
            newErrors.title = t("Title is required");
        } else if (creatifyParams.title.trim().length < 3) {
            newErrors.title = t("Title must be at least characters long", { length: 3 });
        }

        if (!creatifyParams.description?.trim()) {
            newErrors.description = t("Description is required");
        } else if (creatifyParams.description.trim().length < 5) {
            newErrors.description = t("Description must be at least characters long", { length: 5 });
        }

        if (!mediaUrls || mediaUrls.length === 0) {
            newErrors.media = t("At least one media file is required");
        } else if (mediaUrls.length > 5) {
            newErrors.media = t("Maximum media files are allowed", { limit: 5 });
        }

        return newErrors;
    };

    /**
     * Handles input field value changes and triggers validation
     * @param {string} key - The field name to update
     * @param {string} value - The new field value
     */
    const handleChangeValue = (key, value) => {
        const newCreatifyParams = {
            ...creatifyParams,
            [key]: value
        }
        setCreatifyParams(newCreatifyParams)
        const newErrors = validateFields(newCreatifyParams, mediaUrls);
        setErrors(newErrors)
    };

    /**
     * Handles selection/deselection of individual media items
     * @param {string} url - The media URL to toggle selection for
     */
    const handleSelectMedia = (url) => {
        if(itemInfo?.isCached) return
        const isSelected = mediaUrls.includes(url);
        let newMedialUrls = [...mediaUrls]
        if (isSelected) {
            newMedialUrls = newMedialUrls.filter(item => item !== url)
        } else {
            // Check if adding this media would exceed the limit of 5
            if (newMedialUrls.length >= 5) {
                // Don't add more media if already at limit
                return;
            }
            newMedialUrls = [...newMedialUrls, url]
        }
        setErrors(validateFields(creatifyParams, newMedialUrls))
        setMediaUrls(newMedialUrls)
    }

    /**
     * Handles selecting or deselecting all media items (max 5)
     */
    const handleSelectAll = () => {
        if(itemInfo?.isCached) return
        const allUrls = itemInfo?.itemImageURLs || [];
        const isAllSelected = allUrls.length > 0 && allUrls.every(url => mediaUrls.includes(url));

        if (isAllSelected) {
            // Deselect all
            setMediaUrls([]);
            setErrors(validateFields(creatifyParams, []));
        } else {
            // Select up to 5 items maximum
            const urlsToSelect = allUrls.slice(0, 5);
            setMediaUrls(urlsToSelect);
            setErrors(validateFields(creatifyParams, urlsToSelect));
        }
    };

    /**
     * Checks if the form has valid data for submission
     * @returns {boolean} True if form is valid, false otherwise
     */
    const isFormValid = () => {
        return (
            creatifyParams?.title?.trim() &&
            creatifyParams?.title?.trim()?.length >= 3 &&
            creatifyParams?.description?.trim() &&
            creatifyParams?.description?.trim()?.length >= 5 &&
            mediaUrls?.length > 0 && mediaUrls?.length <= 5
        );
    };

    /**
     * Handles form submission and creates Creatify link with parameters
     */
    const handleSubmit = async () => {
        const validationErrors = validateFields(creatifyParams, mediaUrls);
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return
        }
        try {
            setLoading(true)
            const { imgUrls, videoUrls } = separateMediaUrlsByType(mediaUrls, [], false)
            const creatifyPayload = {
                title: creatifyParams?.title,
                description: creatifyParams?.description,
                image_urls: imgUrls,
                video_urls: videoUrls,
                itemId:itemId,
                brandId:user?.brandId,
                token:user?.token,
                userRefId:user?.userId
            }
            const creatifyResponse = await CreateCreatifyLinkWithParams(creatifyPayload)
            onNext(stepsEnum.stepTwo,{...creatifyResponse})
        } catch (error) {
        }finally{
            setLoading(false)
        }
    };

    const fields = [
        {
            label: "Title",
            placeholder: "Enter title",
            name: "title",
            type: "text",
            as: "Input",
            required: true,
        },
        {
            label: "Description",
            placeholder: "Enter description",
            name: "description",
            type: "text",
            as: "TextArea",
            required: true,
            props: {
                rows: 10,
            },
        },
    ];

    const disabled = !isFormValid() || loading

    return (
        <Row gutter={[24, 24]}>
            {/* Media Gallery */}
            <Col span={12} className="h-full">
                <Card
                    title={t("Media Gallery")}
                    className="h-full"
                    extra={
                        <div className="flex items-center gap-4">
                            <div className="text-gray-600 text-sm">
                                {t("Selected")}: {mediaUrls.length} / {Math.min(itemInfo?.itemImageURLs?.length || 0, 5)} {t("(Max 5)")}
                            </div>
                            <TailwindButton
                                onClick={handleSelectAll}
                                disabled={!itemInfo?.itemImageURLs || itemInfo?.itemImageURLs?.length === 0}
                                text={mediaUrls.length === Math.min(itemInfo?.itemImageURLs?.length || 0, 5) && mediaUrls.length > 0
                                    ? "Deselect All"
                                    : "Select All"}
                            />
                        </div>
                    }
                    bordered={false}
                    headStyle={headerStyles}
                    bodyStyle={{
                        height: "calc(100vh - 350px)",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "24px 4px",
                    }}
                >
                    {errors.media && (
                        <div className="text-red-500 text-sm mb-2 px-4">
                            {errors.media}
                        </div>
                    )}
                    <Row gutter={[16, 16]} className="p-4 w-full">
                        {itemInfo?.itemImageURLs?.length > 0 ? (
                            itemInfo?.itemImageURLs?.map((url, index) => {
                                const isSelected = mediaUrls.includes(url);
                                return (
                                    <Col xs={12} sm={12} md={8} key={index}>
                                       <MediaSelectorCard url={url} isSelected={isSelected} isMaxLimitReached={mediaUrls.length >= 5} handleSelectMedia={handleSelectMedia} />
                                    </Col>
                                );
                            })
                        ) : (
                            <Col span={12} className="flex gap-2 items-center justify-center">
                                <p className="text-gray-500">
                                    {t("No media found, Please upload media first")}
                                </p>
                                <TailwindButton
                                    text={"Upload Item"}
                                    onClick={() =>
                                        router.push(itemRoutesUrl.EditItem, {
                                            itemId,
                                            itemInfo,
                                            ...router?.location?.state,
                                        })
                                    }
                                />
                            </Col>
                        )}
                    </Row>
                </Card>
            </Col>
            {/* Content Details */}
            <Col span={12} className="h-full">
                <Card
                    title={t("Content Details")}
                    className="h-full flex flex-col gap-4"
                    bordered={false}
                    headStyle={headerStyles}
                >
                    {fields?.map((field, index) => {
                        const Tag = field?.as === "TextArea" ? TextArea : Input;

                        return (
                            <div key={index} className={`flex flex-col gap-1 ${index === fields?.length - 1 ? 'mb-0' : 'mb-3'}`}>
                                <label className="font-bold text-15x">
                                    {t(field?.label)}{" "}
                                    {field?.required ? (
                                        <span className="text-red-400">*</span>
                                    ) : null}
                                </label>
                                <Tag
                                    name={field?.name}
                                    id={field?.name}
                                    placeholder={t(field?.placeholder)}
                                    value={creatifyParams?.[field?.name] || ""}
                                    onChange={(e) => handleChangeValue(field?.name, e.target.value)}
                                    status={errors?.[field?.name] ? "error" : ""}
                                    {...(field?.props || {})}
                                    disabled={field?.disabled}
                                />
                                {errors?.[field?.name] && (
                                    <div className="text-red-500 text-sm">
                                        {errors?.[field?.name]}
                                    </div>
                                )}

                            </div>
                        )
                    })}

                    <div className="mt-auto pt-4">
                        <TailwindButton
                            text={"Save & Continue"}
                            disabled={disabled}
                            onClick={handleSubmit}
                        />
                    </div>
                </Card>
            </Col>
        </Row>
    );
};

export default StepOne;
